import {useEffect, useRef, useState} from 'react';
import {Check as CheckIcon, X as XIcon} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {Tooltip} from '@/matic/components/ui/tooltip';
import type {ChipAssociation} from '../../types/relay-types';
import {AssociationLabel} from './AssociationLabel';

type AssociationChipProps = {
  assoc: ChipAssociation;
  /** Visual tone — `success` for confirmed associations, `neutral` otherwise. */
  tone?: 'neutral' | 'success';
  /** Prefix the label with "{objectTypeName}: " — used in roomier surfaces. */
  includeTypeName?: boolean;
  /**
   * Render a subtle outline marking this chip as an anchor (e.g. a required-type
   * association that gates confirmation in the thread detail). Off by default —
   * most surfaces render borderless chips.
   */
  emphasized?: boolean;
  /** For a type-only chip `objectRecordId` is null — navigate to the Category scope. */
  onNavigate: (objectTypeId: string, objectRecordId: string | null) => void;
  /** Omit to render a non-removable chip (e.g. once confirmed). */
  onRemove?: (associationId: string) => void;
  /** Promote a `suggested` chip to a committed association. */
  onAccept?: (associationId: string) => void;
  /** Dismiss a `suggested` chip. Rejection is durable — it won't resurface. */
  onReject?: (associationId: string) => void;
};

/** Resting max width of the label (truncated) and the hover-expanded cap. */
const RESTING_MAX_W = 200;
const EXPANDED_MAX_W = 460;
/** Sustained-hover delay before the chip expands, so a passing cursor is calm. */
const HOVER_EXPAND_DELAY_MS = 400;

/**
 * The chip's four mutually exclusive looks, most specific first: a staged
 * proposal, a Category-only link, a confirmed link, a plain link.
 */
type ChipVariant = 'suggested' | 'typeOnly' | 'success' | 'neutral';

const SHELL_CLASSES: Record<ChipVariant, string> = {
  suggested: 'border-primary-200 bg-primary-50 text-primary-700',
  typeOnly: 'border-dashed border-neutral-300 bg-neutral-50 text-neutral-500',
  success: 'bg-success-50 text-success-700',
  neutral: 'bg-neutral-100 text-neutral-600',
};

const LABEL_HOVER_CLASSES: Record<ChipVariant, string> = {
  suggested: 'hover:bg-primary-100',
  typeOnly: 'hover:bg-neutral-200',
  success: 'hover:bg-success-100',
  neutral: 'hover:bg-neutral-200',
};

function chipVariant(assoc: ChipAssociation, isSuccess: boolean): ChipVariant {
  if (assoc.status === 'suggested') return 'suggested';
  if (assoc.objectRecordId === null) return 'typeOnly';
  return isSuccess ? 'success' : 'neutral';
}

/**
 * The outline that marks an anchor chip. Only a plain link carries one —
 * suggested and Category-only chips already own their border.
 */
function borderClass(variant: ChipVariant, emphasized: boolean): string {
  if (variant === 'suggested' || variant === 'typeOnly') return '';
  if (!emphasized) return 'border-transparent';
  return variant === 'success' ? 'border-success-200' : 'border-neutral-300';
}

/**
 * The canonical association chip used across the app — triage thread/file cards,
 * the thread detail, and Home. A click navigates to the record; a hover-✕
 * removes it (when `onRemove` is provided). Long labels truncate with an
 * ellipsis at rest, then expand smoothly to reveal the full text on a debounced
 * sustained hover (collapsing again on leave).
 *
 * Three states beyond the plain link, each carrying how sure the system was:
 * type-only (dashed, "a Category, no specific record"), medium-confidence
 * (softened, "worth a second look"), and suggested (accented, with inline
 * accept/reject — not a link at all until someone says so).
 */
export function AssociationChip({
  assoc,
  tone = 'neutral',
  includeTypeName = false,
  emphasized = false,
  onNavigate,
  onRemove,
  onAccept,
  onReject,
}: AssociationChipProps) {
  const variant = chipVariant(assoc, tone === 'success');
  const isSuggested = variant === 'suggested';
  // A medium-band commit is a real link the system wasn't sure about. Softer
  // than a confident chip, but never the dashed treatment — dashed already
  // means "resolved but general", which is a different thing.
  const isTentative =
    (variant === 'success' || variant === 'neutral') &&
    assoc.confidenceBand === 'medium';
  const [expanded, setExpanded] = useState(false);
  // Locked to the chip's resting rendered size (both axes — see the pill's
  // `expanded && 'absolute'` below: once the pill leaves flow, a
  // width-only lock still lets the shell's height collapse to zero and the
  // row shift) for the duration of a hover expansion, so the shell's
  // footprint in the flex-wrap row never changes.
  const [lockedSize, setLockedSize] = useState<
    {width: number; height: number} | undefined
  >(undefined);
  const shellRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  };

  useEffect(() => clearTimer, []);

  const collapse = () => {
    clearTimer();
    setExpanded(false);
    setLockedSize(undefined);
  };

  // Split into a "shell" (sized to the resting width, never resizes — this is
  // what the flex-wrap row lays out) and a "pill" (the visible chip, which on
  // expansion becomes an absolutely-positioned overlay pinned to the shell's
  // top-left corner). Without this split, growing max-width in place could
  // push the chip onto the row's next line, moving it out from under the
  // cursor — which fires mouseleave, collapses it, and snaps it back, in an
  // endless expand/collapse loop. Since the overlay stays a DOM descendant of
  // the shell, hovering it (even where it visually overflows the shell's
  // pinned-width box) still counts as hovering the shell, so no bogus
  // mouseleave fires and neither the shell nor any sibling ever moves.
  const chip = (
    <span
      ref={shellRef}
      onMouseEnter={() => {
        clearTimer();
        timerRef.current = setTimeout(() => {
          const rect = shellRef.current?.getBoundingClientRect();
          setLockedSize(rect && {width: rect.width, height: rect.height});
          setExpanded(true);
        }, HOVER_EXPAND_DELAY_MS);
      }}
      onMouseLeave={collapse}
      style={lockedSize}
      className="relative inline-block align-middle"
    >
      <span
        style={{maxWidth: expanded ? EXPANDED_MAX_W : RESTING_MAX_W}}
        className={cn(
          'group/chip inline-flex items-center rounded-full border text-xs font-medium transition-[max-width] duration-300 ease-out motion-reduce:transition-none',
          SHELL_CLASSES[variant],
          isTentative && 'opacity-75',
          borderClass(variant, emphasized),
          expanded && 'absolute left-0 top-0 z-20 shadow-md'
        )}
      >
        <button
          type="button"
          // `truncate` must sit on the flex-item button, not the inner inline
          // <span> — overflow/ellipsis don't apply to inline elements, so the
          // label overflowed the chip. min-w-0 lets it shrink below content.
          className={cn(
            'block min-w-0 truncate rounded-full px-2 py-0.5 transition-colors',
            LABEL_HOVER_CLASSES[variant]
          )}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(assoc.objectTypeId, assoc.objectRecordId);
          }}
        >
          <AssociationLabel assoc={assoc} includeTypeName={includeTypeName} />
        </button>
        {isSuggested && onAccept && (
          <button
            type="button"
            aria-label="Accept suggestion"
            className="hidden rounded-full p-0.5 text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-700 group-hover/chip:inline-flex"
            onClick={(e) => {
              e.stopPropagation();
              onAccept(assoc.id);
            }}
          >
            <CheckIcon className="h-2.5 w-2.5" />
          </button>
        )}
        {isSuggested && onReject && (
          <button
            type="button"
            aria-label="Reject suggestion"
            className="mr-0.5 hidden rounded-full p-0.5 text-primary-400 transition-colors hover:bg-primary-100 hover:text-primary-700 group-hover/chip:inline-flex"
            onClick={(e) => {
              e.stopPropagation();
              onReject(assoc.id);
            }}
          >
            <XIcon className="h-2.5 w-2.5" />
          </button>
        )}
        {!isSuggested && onRemove && (
          <button
            type="button"
            aria-label="Remove association"
            className="mr-0.5 hidden rounded-full p-0.5 text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600 group-hover/chip:inline-flex"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(assoc.id);
            }}
          >
            <XIcon className="h-2.5 w-2.5" />
          </button>
        )}
      </span>
    </span>
  );

  return assoc.reason ? <Tooltip content={assoc.reason}>{chip}</Tooltip> : chip;
}
